import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Mail, Phone, MapPin, CheckCircle2, ArrowRight, ExternalLink, ShieldCheck, Lock,
} from "lucide-react";
import { CONTACT_INFO, GOOGLE_FORM_URL } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two variants:
 * - default ("registration"): Short interest form. On success shows next-step screen
 *   directing the visitor to the external Google Form for full traveller registration.
 * - "contact": General contact form. Simple success message, no Google Form CTA.
 *
 * SENSITIVE FIELDS (passport, DOB, medical, nationality, etc.) are NEVER collected here.
 */
export default function RegistrationForm({ variant = "registration" }) {
  const isContact = variant === "contact";
  const endpoint = isContact ? "/contact" : "/registrations";

  const [form, setForm] = useState({
    full_name: "",
    name: "",
    email: "",
    phone: "",
    city_country: "",
    travelers: "1",
    trip: "October 2026 Umrah",
    room_type: "Double (Standard)",
    topic: "October 2026 Umrah",
    message: "",
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: "" });
    try {
      const payload = isContact
        ? {
            name: form.name || form.full_name,
            email: form.email,
            phone: form.phone,
            topic: form.topic,
            message: form.message,
          }
        : {
            full_name: form.full_name,
            email: form.email,
            phone: form.phone,
            city_country: form.city_country,
            travelers: form.travelers,
            trip: form.trip,
            room_type: form.room_type,
            message: form.message,
          };
      const res = await axios.post(`${API}${endpoint}`, payload);
      if (res.data?.id) {
        setStatus({ loading: false, success: true, error: "" });
        setSubmitted({
          name: form.full_name || form.name,
          email: form.email,
        });
        if (isContact) {
          setForm((p) => ({ ...p, name: "", email: "", phone: "", message: "" }));
        }
      } else {
        setStatus({ loading: false, success: false, error: "Submission failed. Please try again." });
      }
    } catch (err) {
      setStatus({
        loading: false,
        success: false,
        error: err?.response?.data?.detail || "Submission failed. Please try again.",
      });
    }
  };

  const inputCls =
    "w-full border border-[var(--maqam-border)] bg-white px-4 py-3 rounded-lg focus:outline-none focus:border-[var(--maqam-navy)] transition-colors text-sm";

  return (
    <section
      data-testid={isContact ? "contact-form-section" : "registration-form-section"}
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">{isContact ? "Get In Touch" : "Register Interest"}</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[color:var(--maqam-navy)] mt-3 leading-[1.05] tracking-tight">
              {isContact ? "We'd love to hear from you" : "Start your Umrah journey"}
            </h2>
            <p className="mt-6 text-[var(--maqam-muted)] leading-relaxed">
              {isContact
                ? "Questions about a trip, our community programs, or anything else — drop us a note and we'll reply."
                : "This short form lets us know you're interested. A team member will follow up with pricing, payment plan options, and next steps. Full traveller registration is completed via our secure Google Form."}
            </p>

            {!isContact && (
              <div
                data-testid="interest-form-privacy-note"
                className="mt-8 rounded-2xl border border-[var(--maqam-border)] bg-[var(--maqam-surface)] p-5"
              >
                <p className="flex items-center gap-2 text-sm font-semibold text-[color:var(--maqam-navy)]">
                  <ShieldCheck size={16} className="text-[color:var(--maqam-gold)]" /> Privacy first
                </p>
                <p className="mt-2 text-xs text-[var(--maqam-muted)] leading-relaxed">
                  We only collect the fields shown on this form. No passport, medical, or date-of-birth
                  information is stored on this website — those details are handled through our secure Google Form
                  and reviewed only by authorized Maqam staff.{" "}
                  <Link
                    to="/privacy"
                    data-testid="interest-form-privacy-link"
                    className="underline underline-offset-2 decoration-[color:var(--maqam-gold)] font-medium"
                  >
                    Read the Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            )}

            <div className="mt-8 space-y-4 text-sm">
              <p className="flex items-center gap-3 text-[color:var(--maqam-ink)]"><MapPin size={16} className="text-[color:var(--maqam-gold)]" /> {CONTACT_INFO.location}</p>
              <p className="flex items-center gap-3 text-[color:var(--maqam-ink)]"><Mail size={16} className="text-[color:var(--maqam-gold)]" /> <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-[color:var(--maqam-navy)] underline underline-offset-2 decoration-transparent hover:decoration-[color:var(--maqam-gold)]">{CONTACT_INFO.email}</a></p>
              {CONTACT_INFO.regional.map((r) => (
                <p key={r.label} className="flex items-center gap-3 text-[color:var(--maqam-ink)]">
                  <Phone size={16} className="text-[color:var(--maqam-gold)]" />
                  <span className="text-[var(--maqam-muted)] text-xs">{r.label.replace(" Regional Contact", "")}:</span>
                  <a href={r.telHref} className="font-medium hover:text-[color:var(--maqam-navy)]">{r.displayPhone}</a>
                </p>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            {status.success && !isContact ? (
              /* -------- NEXT-STEP SUCCESS SCREEN (registration variant only) -------- */
              <div
                data-testid="registration-next-step"
                className="review-shadow rounded-3xl border border-[var(--maqam-border)] bg-white p-8 md:p-12"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6" style={{ background: "rgba(200,162,74,0.16)" }}>
                  <CheckCircle2 size={28} className="text-[color:var(--maqam-gold)]" />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-[color:var(--maqam-navy)] leading-tight tracking-tight">
                  Thank you{submitted?.name ? `, ${submitted.name.split(" ")[0]}` : ""}.
                </h3>
                <p className="mt-4 text-[var(--maqam-ink)] text-base md:text-lg leading-relaxed max-w-xl">
                  We've received your interest. To complete your traveller registration,
                  please continue to our secure registration form.
                </p>

                <div className="mt-8">
                  <a
                    href={GOOGLE_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="complete-registration-cta"
                    className="btn-gold rounded-full px-7 py-4 font-semibold inline-flex items-center gap-2 text-base"
                  >
                    <Lock size={16} /> Complete Traveller Registration <ExternalLink size={16} />
                  </a>
                  <p className="mt-3 text-xs text-[var(--maqam-muted)]">Opens our secure Google Form in a new tab.</p>
                </div>

                <div
                  data-testid="registration-consent"
                  className="mt-8 rounded-2xl bg-[var(--maqam-surface)] border border-[var(--maqam-border)] p-5"
                >
                  <p className="flex items-start gap-2 text-xs text-[var(--maqam-muted)] leading-relaxed">
                    <ShieldCheck size={14} className="text-[color:var(--maqam-gold)] mt-0.5 flex-shrink-0" />
                    <span>
                      Traveller information is collected for registration, travel coordination,
                      accommodation, transportation, safety, and related trip administration. Please
                      review our{" "}
                      <Link to="/privacy" className="underline underline-offset-2 decoration-[color:var(--maqam-gold)] font-medium">
                        Privacy Policy
                      </Link>{" "}
                      before submitting.
                    </span>
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-[var(--maqam-border)] flex flex-col sm:flex-row gap-3 text-sm">
                  <button
                    onClick={() => {
                      setStatus({ loading: false, success: false, error: "" });
                      setSubmitted(null);
                      setForm({
                        full_name: "", name: "", email: "", phone: "",
                        city_country: "", travelers: "1", trip: "October 2026 Umrah",
                        room_type: "Double (Standard)", topic: "October 2026 Umrah", message: "",
                      });
                    }}
                    data-testid="interest-form-reset"
                    className="text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] font-medium"
                  >
                    Submit another interest
                  </button>
                  <span className="hidden sm:inline text-[var(--maqam-muted)]">·</span>
                  <Link
                    to="/"
                    data-testid="interest-form-home"
                    className="text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] font-medium"
                  >
                    Return to homepage
                  </Link>
                </div>
              </div>
            ) : status.success && isContact ? (
              <div
                data-testid="contact-success"
                className="review-shadow rounded-3xl border border-[var(--maqam-border)] bg-white p-10 text-center"
              >
                <CheckCircle2 size={40} className="text-[color:var(--maqam-gold)] mx-auto" />
                <h3 className="mt-5 font-serif text-3xl text-[color:var(--maqam-navy)]">Thank you.</h3>
                <p className="mt-3 text-[var(--maqam-muted)]">
                  We received your message and will reply as soon as we can.
                </p>
                <button
                  onClick={() => {
                    setStatus({ loading: false, success: false, error: "" });
                    setSubmitted(null);
                  }}
                  data-testid="contact-form-reset"
                  className="mt-6 text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] font-medium text-sm"
                >
                  Send another message
                </button>
              </div>
            ) : (
              /* -------- FORM (initial state) -------- */
              <form
                onSubmit={submit}
                data-testid={isContact ? "contact-form" : "registration-form"}
                className="review-shadow rounded-3xl border border-[var(--maqam-border)] bg-white p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <div className="md:col-span-1">
                  <label className="overline mb-2 block">Full Name</label>
                  <input
                    data-testid="form-name"
                    required
                    className={inputCls}
                    value={isContact ? form.name : form.full_name}
                    onChange={(e) => update(isContact ? "name" : "full_name", e.target.value)}
                    placeholder="Your full name"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="overline mb-2 block">Email</label>
                  <input
                    data-testid="form-email"
                    type="email"
                    required
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="overline mb-2 block">Phone</label>
                  <input
                    data-testid="form-phone"
                    required={!isContact}
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (___) ___-____"
                  />
                </div>

                {!isContact ? (
                  <>
                    <div className="md:col-span-1">
                      <label className="overline mb-2 block">City &amp; Country</label>
                      <input
                        data-testid="form-city-country"
                        required
                        className={inputCls}
                        value={form.city_country}
                        onChange={(e) => update("city_country", e.target.value)}
                        placeholder="e.g. Ottawa, Canada"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <label className="overline mb-2 block">Number of Travellers</label>
                      <select
                        data-testid="form-travelers"
                        className={inputCls}
                        value={form.travelers}
                        onChange={(e) => update("travelers", e.target.value)}
                      >
                        {["1", "2", "3", "4", "5", "6+"].map((n) => <option key={n}>{n}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-1">
                      <label className="overline mb-2 block">Trip of Interest</label>
                      <select
                        data-testid="form-trip"
                        className={inputCls}
                        value={form.trip}
                        onChange={(e) => update("trip", e.target.value)}
                      >
                        <option>October 2026 Umrah</option>
                        <option>Future Trip — Notify Me</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="overline mb-2 block">Preferred Room Type</label>
                      <select
                        data-testid="form-room-type"
                        className={inputCls}
                        value={form.room_type}
                        onChange={(e) => update("room_type", e.target.value)}
                      >
                        <option>Quad (Most Affordable)</option>
                        <option>Triple</option>
                        <option>Double (Standard)</option>
                        <option>Single (Premium)</option>
                        <option>Not sure yet</option>
                      </select>
                    </div>
                  </>
                ) : (
                  <div className="md:col-span-1">
                    <label className="overline mb-2 block">Topic</label>
                    <select
                      data-testid="form-topic"
                      className={inputCls}
                      value={form.topic}
                      onChange={(e) => update("topic", e.target.value)}
                    >
                      <option>October 2026 Umrah</option>
                      <option>Community Initiatives</option>
                      <option>Seminars &amp; Events</option>
                      <option>General</option>
                    </select>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="overline mb-2 block">Message (optional)</label>
                  <textarea
                    data-testid="form-message"
                    rows={4}
                    className={inputCls + " resize-none"}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Anything we should know…"
                    required={isContact}
                  />
                </div>

                {status.error && (
                  <p data-testid="form-error" className="md:col-span-2 text-sm text-red-600">{status.error}</p>
                )}

                <div className="md:col-span-2 pt-2">
                  <button
                    data-testid="form-submit"
                    type="submit"
                    disabled={status.loading}
                    className="btn-gold rounded-full px-7 py-3 font-semibold disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    {status.loading
                      ? "Submitting…"
                      : isContact
                        ? "Send Message"
                        : (<>Submit Interest <ArrowRight size={16} /></>)
                    }
                  </button>
                  {!isContact && (
                    <p className="mt-4 text-xs text-[var(--maqam-muted)] leading-relaxed max-w-xl">
                      Submitting confirms your interest only. After this, we'll direct you to the secure
                      Google Form to complete your traveller registration.{" "}
                      <Link
                        to="/privacy"
                        data-testid="form-privacy-inline"
                        className="underline underline-offset-2 decoration-[color:var(--maqam-gold)]"
                      >
                        Privacy Policy
                      </Link>.
                    </p>
                  )}
                </div>

                {!isContact && (
                  <div className="md:col-span-2 pt-2 border-t border-[var(--maqam-border)] mt-2">
                    <p className="text-xs text-[var(--maqam-muted)] mb-2">Already decided?</p>
                    <a
                      href={GOOGLE_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="direct-google-form-link"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)]"
                    >
                      <Lock size={14} /> Skip ahead and complete traveller registration <ExternalLink size={13} />
                    </a>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
